import { runInAction } from "mobx";
import type { IWarehouseApi } from "../../../api/WarehouseApi";
import type { Product } from "../../../stores/Product";
import type { ProductsListFilterState } from "./filters";

export interface ProductsListPagingState extends ProductsListFilterState {
  items: Product[];
  total: number;
  offset: number;
  isLoadingInitial: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  isBusy: boolean;
}

function mergeProducts(current: Product[], next: Product[]): Product[] {
  const merged = new Map<string, Product>();

  current.forEach((item) => {
    merged.set(item.id, item);
  });

  next.forEach((item) => {
    merged.set(item.id, item);
  });

  return Array.from(merged.values());
}

export async function refreshProductsList(input: {
  state: ProductsListPagingState;
  loadMore: () => Promise<void>;
}): Promise<void> {
  runInAction(() => {
    input.state.items = [];
    input.state.total = 0;
    input.state.offset = 0;
    input.state.hasMore = true;
    input.state.error = null;
  });

  await input.loadMore();
}

export async function loadMoreProductsList(input: {
  state: ProductsListPagingState;
  api: IWarehouseApi;
  pageSize: number;
  toProduct: (
    raw: Awaited<ReturnType<IWarehouseApi["getProductsPage"]>>["items"][number],
  ) => Product;
}): Promise<void> {
  if (!input.state.hasMore || input.state.isBusy) {
    return;
  }

  const isInitial = input.state.items.length === 0;

  runInAction(() => {
    input.state.error = null;
    if (isInitial) {
      input.state.isLoadingInitial = true;
    } else {
      input.state.isLoadingMore = true;
    }
  });

  try {
    const page = await input.api.getProductsPage({
      limit: input.pageSize,
      offset: input.state.offset,
      placement: input.state.placementFilter,
      expiry: input.state.expiryFilter,
      expiringDays: input.state.expiringDays,
      measure: input.state.measureFilter,
      q: input.state.queryFilter,
      typeTag: input.state.typeTagFilter || undefined,
    });
    const loadedItems = page.items.map((item) => input.toProduct(item));

    runInAction(() => {
      input.state.items = mergeProducts(input.state.items, loadedItems);
      input.state.total = page.total;
      input.state.offset = page.offset + page.items.length;
      input.state.hasMore =
        page.items.length > 0 && input.state.items.length < page.total;
    });
  } catch (error) {
    runInAction(() => {
      input.state.error =
        error instanceof Error
          ? error.message
          : "Не удалось загрузить список товаров";
    });
  } finally {
    runInAction(() => {
      input.state.isLoadingInitial = false;
      input.state.isLoadingMore = false;
    });
  }
}
